// Third-party
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

// Store
import { useSelector } from 'store';
import { cn } from 'utils/cn';

// Project
import CancelButton from 'components/buttons/CancelButton';
import SaveButton from 'components/buttons/SaveButton';

interface Props {
    onCancel: () => void;
    onSave: () => void;
    extraButton?: JSX.Element;
    saveButtonTextId?: string;
    /** What the save label interpolates, e.g. a count. */
    saveButtonValues?: Record<string, string | number>;
    cancelButtonTextId?: string;
    savePermissions?: string[];
    saveHasAuthority?: boolean;
    readOnly?: boolean;
    disableSave?: boolean;
    disabledSaveInfoTextId?: string;
    height?: number;
    /** In the new design, the kit dialog's footer: no rule or fill, its own height. Classic is unchanged. */
    kit?: boolean;
    /** Opt-in: a line at the start of the row, before the buttons (what the main button will act on). */
    note?: ReactNode;
    /** Opt-in: a second answer between Cancel and the main button. */
    secondaryButton?: JSX.Element;
}

/** The kit dialog's footer, in the new design only; `h-auto!` outweighs the classic footer's inline height. */
export const KIT_FOOTER = 'new:h-auto! new:border-t-0 new:bg-transparent new:px-6 new:pt-0 new:pb-6';

export const ModalFooter = ({
    onCancel,
    onSave,
    extraButton,
    saveButtonTextId,
    saveButtonValues,
    cancelButtonTextId,
    savePermissions,
    saveHasAuthority,
    disabledSaveInfoTextId,
    readOnly,
    disableSave = false,
    height = 80,
    kit,
    note,
    secondaryButton,
}: Props): JSX.Element => {
    const buttonsDisabled = useSelector((state) => state.buttons?.buttonsDisabled);
    const saveDisabled = disableSave || buttonsDisabled;

    return (
        <div
            className={cn(
                'border-divider bg-grey-0 flex items-center rounded-b-[12px] border-t px-6 py-2',
                kit && KIT_FOOTER
            )}
            style={{ height }}
        >
            <div className={cn('flex w-full flex-row items-center', extraButton ? 'justify-between' : 'justify-end')}>
                {disableSave && disabledSaveInfoTextId ? (
                    <span className="text-error mr-4 text-xs font-normal text-wrap">
                        <FormattedMessage id={disabledSaveInfoTextId} />
                    </span>
                ) : null}
                {note ? (
                    <span className="text-meta text-foreground me-auto font-semibold tabular-nums">{note}</span>
                ) : null}
                {extraButton ? extraButton : null}
                <div className="flex flex-row gap-2">
                    <CancelButton onClick={onCancel} disabled={buttonsDisabled}>
                        <FormattedMessage id={cancelButtonTextId ?? 'cancel'} />
                    </CancelButton>
                    {secondaryButton}
                    <SaveButton
                        permissions={savePermissions}
                        hasAuthority={saveHasAuthority}
                        onClick={onSave}
                        disabled={saveDisabled || readOnly}
                    >
                        <FormattedMessage id={saveButtonTextId ?? 'save'} values={saveButtonValues} />
                    </SaveButton>
                </div>
            </div>
        </div>
    );
};
